import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRelances, getDossiers, updateRelance, deleteRelance } from '../lib/api';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { 
    Calendar as CalendarIcon, 
    Clock, 
    Phone, 
    Mail, 
    MessageSquare,
    CheckCircle2,
    XCircle,
    ChevronLeft,
    ChevronRight,
    HardHat,
    FileText
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, isSameDay, parseISO, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

export const AgendaPage = () => {
    const [relances, setRelances] = useState([]);
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [relancesRes, dossiersRes] = await Promise.all([
                getRelances({ statut: 'planifiee' }),
                getDossiers()
            ]);
            setRelances(relancesRes.data);
            setDossiers(dossiersRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRelanceDone = async (relanceId) => {
        try {
            await updateRelance(relanceId, { 
                statut: 'envoyee', 
                date_envoi: new Date().toISOString() 
            });
            toast.success('Relance marquée comme effectuée');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDeleteRelance = async (relanceId) => {
        try {
            await deleteRelance(relanceId);
            toast.success('Relance supprimée');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    // Get events for a specific date
    const getEventsForDate = (date) => {
        const events = [];
        
        // Relances
        relances.forEach(r => {
            if (r.date_prevue && isSameDay(parseISO(r.date_prevue), date)) {
                events.push({
                    type: 'relance',
                    data: r,
                    color: 'bg-[#D97706]'
                });
            }
        });
        
        // RDVs
        dossiers.forEach(d => {
            if (d.date_rdv && isSameDay(parseISO(d.date_rdv), date)) {
                events.push({
                    type: 'rdv',
                    data: d,
                    color: 'bg-[#E85A24]'
                });
            }
            if (d.date_debut_chantier && isSameDay(parseISO(d.date_debut_chantier), date)) {
                events.push({
                    type: 'chantier_debut',
                    data: d,
                    color: 'bg-[#16A34A]'
                });
            }
            if (d.date_fin_chantier && isSameDay(parseISO(d.date_fin_chantier), date)) {
                events.push({
                    type: 'chantier_fin',
                    data: d,
                    color: 'bg-[#0284C7]'
                });
            }
        });
        
        return events;
    };

    // Get all events for selected date
    const selectedDateEvents = getEventsForDate(selectedDate);

    // Get dates with events for calendar highlighting
    const getDatesWithEvents = () => {
        const dates = new Set();
        
        relances.forEach(r => {
            if (r.date_prevue) {
                dates.add(format(parseISO(r.date_prevue), 'yyyy-MM-dd'));
            }
        });
        
        dossiers.forEach(d => {
            if (d.date_rdv) dates.add(format(parseISO(d.date_rdv), 'yyyy-MM-dd'));
            if (d.date_debut_chantier) dates.add(format(parseISO(d.date_debut_chantier), 'yyyy-MM-dd'));
            if (d.date_fin_chantier) dates.add(format(parseISO(d.date_fin_chantier), 'yyyy-MM-dd'));
        });
        
        return dates;
    };

    const datesWithEvents = getDatesWithEvents();

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: fr });
    };

    const getRelanceIcon = (type) => {
        switch (type) {
            case 'email': return <Mail className="w-4 h-4" />;
            case 'sms': return <MessageSquare className="w-4 h-4" />;
            case 'appel': return <Phone className="w-4 h-4" />;
            default: return <Clock className="w-4 h-4" />;
        }
    };

    // Upcoming events (next 7 days)
    const upcomingRelances = relances
        .filter(r => {
            const date = parseISO(r.date_prevue);
            const now = new Date();
            const weekLater = new Date();
            weekLater.setDate(weekLater.getDate() + 7);
            return date >= now && date <= weekLater;
        })
        .sort((a, b) => new Date(a.date_prevue) - new Date(b.date_prevue));

    const overdueRelances = relances.filter(r => {
        const date = parseISO(r.date_prevue);
        return date < new Date();
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="agenda-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-[#0A0A0A] tracking-tight">Agenda</h1>
                <p className="text-[#52525B] mt-1">Gérez vos rendez-vous, relances et échéances</p>
            </div>

            {/* Overdue Alert */}
            {overdueRelances.length > 0 && (
                <div className="bg-[#DC2626]/10 border border-[#DC2626] p-4">
                    <div className="flex items-center gap-2 text-[#DC2626] font-semibold mb-2">
                        <Clock className="w-5 h-5" />
                        {overdueRelances.length} relance(s) en retard
                    </div>
                    <div className="space-y-2">
                        {overdueRelances.slice(0, 3).map(r => (
                            <div key={r.id} className="flex items-center justify-between bg-white p-2 rounded-sm">
                                <div className="flex items-center gap-2">
                                    {getRelanceIcon(r.type_relance)}
                                    <span className="font-medium">{r.prospect_nom || r.dossier_titre}</span>
                                    <span className="text-sm text-[#DC2626]">{formatDate(r.date_prevue)}</span>
                                </div>
                                <div className="flex gap-1">
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleMarkRelanceDone(r.id)}
                                        className="text-[#16A34A]"
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleDeleteRelance(r.id)}
                                        className="text-[#DC2626]"
                                    >
                                        <XCircle className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Calendar */}
                <div className="lg:col-span-2 bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-[#0A0A0A]">
                            {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                        </h2>
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                size="icon"
                                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button 
                                variant="outline" 
                                size="icon"
                                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        month={currentMonth}
                        onMonthChange={setCurrentMonth}
                        locale={fr}
                        className="rounded-sm border-0"
                        modifiers={{
                            hasEvents: (date) => datesWithEvents.has(format(date, 'yyyy-MM-dd'))
                        }}
                        modifiersStyles={{
                            hasEvents: {
                                backgroundColor: '#E85A24',
                                color: 'white',
                                fontWeight: 'bold'
                            }
                        }}
                    />
                    
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#E4E4E7]">
                        <div className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 bg-[#E85A24] rounded-full" />
                            <span>RDV</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 bg-[#D97706] rounded-full" />
                            <span>Relance</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 bg-[#16A34A] rounded-full" />
                            <span>Début chantier</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 bg-[#0284C7] rounded-full" />
                            <span>Fin chantier</span>
                        </div>
                    </div>
                </div>

                {/* Selected Day Events */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-semibold text-[#0A0A0A] mb-4">
                        {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
                    </h3>
                    
                    {selectedDateEvents.length === 0 ? (
                        <div className="text-center py-8 text-[#52525B]">
                            <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Aucun événement ce jour</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {selectedDateEvents.map((event, idx) => (
                                <div key={`${event.type}-${event.data?.id || idx}`} className={`p-3 border-l-4 ${event.color} bg-[#FAFAFA]`}>
                                    {event.type === 'relance' ? (
                                        <div>
                                            <div className="flex items-center gap-2 text-sm font-medium">
                                                {getRelanceIcon(event.data.type_relance)}
                                                Relance {event.data.type_relance}
                                            </div>
                                            <p className="text-sm mt-1">{event.data.prospect_nom || event.data.dossier_titre}</p>
                                            <div className="flex gap-1 mt-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    onClick={() => handleMarkRelanceDone(event.data.id)}
                                                    className="text-[#16A34A] h-7"
                                                >
                                                    <CheckCircle2 className="w-3 h-3 mr-1" /> Fait
                                                </Button>
                                            </div>
                                        </div>
                                    ) : event.type === 'rdv' ? (
                                        <Link to={`/dossiers/${event.data.id}`}>
                                            <div className="flex items-center gap-2 text-sm font-medium">
                                                <CalendarIcon className="w-4 h-4" />
                                                Rendez-vous
                                            </div>
                                            <p className="text-sm mt-1">{event.data.prospect_nom || event.data.titre}</p>
                                        </Link>
                                    ) : event.type === 'chantier_debut' ? (
                                        <Link to={`/dossiers/${event.data.id}`}>
                                            <div className="flex items-center gap-2 text-sm font-medium">
                                                <HardHat className="w-4 h-4" />
                                                Début de chantier
                                            </div>
                                            <p className="text-sm mt-1">{event.data.prospect_nom || event.data.titre}</p>
                                        </Link>
                                    ) : (
                                        <Link to={`/dossiers/${event.data.id}`}>
                                            <div className="flex items-center gap-2 text-sm font-medium">
                                                <HardHat className="w-4 h-4" />
                                                Fin de chantier
                                            </div>
                                            <p className="text-sm mt-1">{event.data.prospect_nom || event.data.titre}</p>
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Upcoming Relances */}
            <div className="bg-white border border-[#E4E4E7] p-6">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">Relances à venir (7 prochains jours)</h3>
                {upcomingRelances.length === 0 ? (
                    <p className="text-[#52525B]">Aucune relance planifiée</p>
                ) : (
                    <div className="space-y-2">
                        {upcomingRelances.map(r => (
                            <div key={r.id} className="flex items-center justify-between p-3 bg-[#FAFAFA] hover:bg-[#F4F4F5] transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#D97706]/10 rounded-full flex items-center justify-center text-[#D97706]">
                                        {getRelanceIcon(r.type_relance)}
                                    </div>
                                    <div>
                                        <p className="font-medium">{r.prospect_nom || r.dossier_titre}</p>
                                        <p className="text-sm text-[#52525B]">
                                            {r.type_relance} • {formatDate(r.date_prevue)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <Link to={`/dossiers/${r.dossier_id}`}>
                                        <Button variant="ghost" size="sm">
                                            <FileText className="w-4 h-4" />
                                        </Button>
                                    </Link>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleMarkRelanceDone(r.id)}
                                        className="text-[#16A34A]"
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleDeleteRelance(r.id)}
                                        className="text-[#DC2626]"
                                    >
                                        <XCircle className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
